/* ------------------------------------------------------------------
   Battle Bottle landing page behaviour.

   Drives the buy box in sections/bb-buybox.liquid:
     - bundle tier selection (price + cart quantity)
     - free-gift lock / unlock
     - colour swatches, plus the second colour picker on multi-packs
     - product accordions
     - building the Shopify cart permalink

   Everything is found via data- attributes, so restyling is safe.
   Re-initialises on Shopify's theme editor events.
   ------------------------------------------------------------------ */
(function () {
  'use strict';

  var SELECTED = ['border-primary-500', 'ring-primary-500', 'ring-1', 'ring-offset-0'];
  var BUNDLE_OFF = ['border-textDark/30'];
  var SWATCH_OFF = ['border-gray-200'];

  function initBuybox(root) {
    if (!root || root.dataset.bbReady === 'true') return;
    root.dataset.bbReady = 'true';

    var configEl = root.querySelector('[data-bb-config]');
    var config = { cartUrl: '/cart', giftUnlockQty: 2, giftVariantIds: [], discountCode: '', variants: [] };
    if (configEl) {
      try { config = JSON.parse(configEl.textContent); } catch (e) { /* keep defaults */ }
    }

    var bundles = Array.prototype.slice.call(root.querySelectorAll('[data-bb-bundle]'));
    var gifts = root.querySelector('[data-bb-gifts]');
    var priceNow = root.querySelector('[data-bb-price-final]');
    var priceWas = root.querySelector('[data-bb-price-compare]');
    var cta = root.querySelector('[data-bb-cta]');
    var pickers = Array.prototype.slice.call(root.querySelectorAll('[data-bb-colour]'));
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-bb-slide]'));

    var quantity = bundles.length ? parseInt(bundles[0].dataset.qty, 10) || 1 : 1;

    /* ---------- helpers ---------- */

    function variantFor(value) {
      for (var i = 0; i < config.variants.length; i++) {
        if (config.variants[i].options.indexOf(value) !== -1) return config.variants[i];
      }
      return null;
    }

    function chosenValue(picker) {
      var current = picker.querySelector('[data-bb-swatch][aria-checked="true"]');
      return current ? current.dataset.value : null;
    }

    function visiblePickers() {
      return pickers.filter(function (p) { return p.getAttribute('data-visible') !== 'false'; });
    }

    function updateCart() {
      if (!cta) return;
      var parts = [];
      visiblePickers().forEach(function (picker) {
        var variant = variantFor(chosenValue(picker));
        if (variant) parts.push(variant.id + ':1');
      });
      if (!parts.length) return;

      /* A multi-pack with only one picker shown falls back to qty on one variant. */
      if (parts.length === 1 && quantity > 1) {
        parts = [parts[0].split(':')[0] + ':' + quantity];
      }

      /* Multi-pack tiers also drop the free gifts in and carry the code that
         zeroes them out. Shopify merges repeated variant IDs on its own. */
      var href = '/cart/' + parts.join(',');
      if (quantity >= (config.giftUnlockQty || 2)) {
        var giftIds = (config.giftVariantIds || []).map(function (id) {
          return String(id).trim();
        }).filter(Boolean);
        if (giftIds.length) {
          href += ',' + giftIds.map(function (id) { return id + ':1'; }).join(',');
        }
        var code = (config.discountCode || '').trim();
        if (code) href += '?discount=' + encodeURIComponent(code);
      }
      cta.setAttribute('href', href);
    }

    function showSlideFor(value) {
      if (!slides.length) return;
      var variant = variantFor(value);
      if (!variant) return;
      /* Match a slide by alt text where possible; otherwise leave the gallery alone. */
      var target = slides.filter(function (slide) {
        var img = slide.querySelector('img');
        return img && img.getAttribute('alt') === variant.title;
      })[0];
      if (!target) return;
      slides.forEach(function (slide) { slide.classList.toggle('bb-hidden', slide !== target); });
    }

    /* ---------- colour swatches ---------- */

    function chooseSwatch(picker, swatch) {
      if (swatch.getAttribute('aria-disabled') === 'true') return;
      var all = Array.prototype.slice.call(picker.querySelectorAll('[data-bb-swatch]'));
      all.forEach(function (other) {
        var on = other === swatch;
        other.setAttribute('aria-checked', on ? 'true' : 'false');
        other.setAttribute('tabindex', on ? '0' : '-1');
        SELECTED.forEach(function (c) { other.classList.toggle(c, on); });
        SWATCH_OFF.forEach(function (c) { other.classList.toggle(c, !on); });
      });
      var label = picker.querySelector('.option__swatch-name');
      if (label) label.textContent = swatch.dataset.value;
      if (picker.dataset.bbColour === '1') showSlideFor(swatch.dataset.value);
      updateCart();
    }

    pickers.forEach(function (picker) {
      var all = Array.prototype.slice.call(picker.querySelectorAll('[data-bb-swatch]'));
      all.forEach(function (swatch, i) {
        if (swatch.getAttribute('aria-disabled') === 'true') {
          swatch.style.cursor = 'not-allowed';
          return;
        }
        swatch.addEventListener('click', function () { chooseSwatch(picker, swatch); });
        swatch.addEventListener('keydown', function (event) {
          var next = null;
          if (event.key === 'Enter' || event.key === ' ') next = i;
          else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (i + 1) % all.length;
          else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (i - 1 + all.length) % all.length;
          if (next === null) return;
          event.preventDefault();
          chooseSwatch(picker, all[next]);
          all[next].focus();
        });
      });
    });

    /* ---------- bundle tiers ---------- */

    function selectBundle(index) {
      bundles.forEach(function (option, i) {
        var on = i === index;
        option.setAttribute('aria-checked', on ? 'true' : 'false');
        option.setAttribute('tabindex', on ? '0' : '-1');
        SELECTED.forEach(function (c) { option.classList.toggle(c, on); });
        BUNDLE_OFF.forEach(function (c) { option.classList.toggle(c, !on); });
      });

      var chosen = bundles[index];
      if (!chosen) return;
      quantity = parseInt(chosen.dataset.qty, 10) || 1;

      if (priceNow) priceNow.textContent = chosen.dataset.price || '';
      if (priceWas) priceWas.textContent = chosen.dataset.compare || '';

      var unlocked = quantity >= (config.giftUnlockQty || 2);
      if (gifts) gifts.setAttribute('data-state', unlocked ? 'unlocked' : 'locked');

      /* Extra colour pickers appear one per additional unit. */
      pickers.forEach(function (picker, i) {
        if (i === 0) return;
        picker.setAttribute('data-visible', quantity > i ? 'true' : 'false');
      });

      updateCart();
    }

    bundles.forEach(function (option, i) {
      option.addEventListener('click', function () { selectBundle(i); });
      option.addEventListener('keydown', function (event) {
        var next = null;
        if (event.key === 'Enter' || event.key === ' ') next = i;
        else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (i + 1) % bundles.length;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (i - 1 + bundles.length) % bundles.length;
        if (next === null) return;
        event.preventDefault();
        selectBundle(next);
        bundles[next].focus();
      });
    });

    /* ---------- accordions ---------- */

    root.querySelectorAll('.details__accordion-item').forEach(function (item) {
      var button = item.querySelector('.disclosure__button');
      var panel = item.querySelector('.disclosure__panel');
      var chevron = item.querySelector('.disclosure__button-icon svg');
      if (!button || !panel) return;

      var open = button.getAttribute('aria-expanded') === 'true';
      var apply = function () {
        button.setAttribute('aria-expanded', open ? 'true' : 'false');
        panel.setAttribute('data-open', open ? 'true' : 'false');
        if (chevron) {
          chevron.classList.toggle('-rotate-180', open);
          chevron.classList.toggle('rotate-0', !open);
        }
      };
      apply();
      button.addEventListener('click', function () { open = !open; apply(); });
    });

    /* ---------- initial state ---------- */

    var startIndex = 0;
    bundles.forEach(function (o, i) {
      if (o.getAttribute('aria-checked') === 'true') startIndex = i;
    });
    selectBundle(startIndex);
  }

  function initAll() {
    document.querySelectorAll('[data-bb-buybox]').forEach(initBuybox);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Shopify theme editor re-renders sections in place. */
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-bb-buybox]');
    if (root) initBuybox(root);
  });
})();
